import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Product {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  preco_custo: number | null;
  status: string;
  foto_url: string | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

export default function PublicProduct() {
  const { id } = useParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["public_product", id],
    queryFn: async () => {
      if (!id) throw new Error("ID inválido");
      const { data, error } = await supabase
        .from("products")
        .select("id, nome, codigo_sku, descricao, preco_venda, preco_custo, status, foto_url")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Product;
    },
    enabled: !!id,
  });

  useEffect(() => {
    // Recarrega se id mudar
    if (id) {
      refetch();
    }
  }, [id, refetch]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Produto não encontrado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">Verifique se o QR code/URL está correto.</p>
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/">Ir para a página inicial</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/20">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{data.nome}</span>
            <Badge className={data.status === "ativo" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}>
              {data.status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="w-full aspect-square overflow-hidden rounded-lg border bg-muted">
              {data.foto_url ? (
                <img src={data.foto_url} alt={data.nome} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">Sem foto</div>
              )}
            </div>
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Código/SKU</div>
                <div className="font-medium">{data.codigo_sku || "—"}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Preço de venda</div>
                <div className="text-xl font-semibold">{formatCurrency(data.preco_venda)}</div>
              </div>
              {data.preco_custo !== null && (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Preço de custo</div>
                  <div className="font-medium">{formatCurrency(data.preco_custo)}</div>
                </div>
              )}
              {data.descricao && (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Descrição</div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{data.descricao}</div>
                </div>
              )}
              <div className="pt-2">
                <Button asChild>
                  <Link to="/auth">Entrar no sistema</Link>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
